import { SEARCH_PAGE } from 'constants/amethystPageTypes';
import { SEARCH_FEEDBACK_CLICK } from 'constants/reduxActions';
import { HYDRA_RET } from 'constants/hydraTests';
import { AUTO_LOGOUT, CLOSE_MODAL_BTN, CLOSE_MODAL_MISC, LOG_OUT_BTN, STAY_SIGNED_IN_BTN } from 'constants/StaySignedInModalInteractionTypes';
import { isAssigned } from 'actions/ab';
import { middlewareTrack } from 'apis/amethyst';
import { getAmethystPageType, trackEvent } from 'helpers/analytics';
import {
  CLOSE_LA,
  LOGIN_FROM_LA,
  REDIRECT_FROM_LA,
  SHOW_LA
} from 'store/ducks/loginAssistant/types';
import { SPONSORED_PRODUCT_EVENT } from 'store/ducks/search/types';
import {
  STORE_REWARDS_TRANSPARENCY_POINTS_FOR_ITEM
} from 'store/ducks/rewards/types';

/*
  Global Events:
  This will allow you to fire events based on client-side actions across the site
*/

const EVENT_CART_MODAL_VIEW = 'EVENT_CART_MODAL_VIEW';

export const cartModalViewEvent = () => ({
  type: EVENT_CART_MODAL_VIEW
});

/**
  * https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/SentimentFeedback.proto
  *
  * @param {boolean} feedback - Customer input on whether search results were helpful -> this comes from the 'feedback' payload from the Redux action
*/
const evFeedbackClick = (_, { feedback }) => {
  middlewareTrack({
    sentimentFeedback: {
      pageType: SEARCH_PAGE,
      feedbackType: 'SEARCH_RELEVANCY_FEEDBACK',
      positive: feedback,
      negative: !feedback
    }
  });
};

const teOnCloseLoginAssistantClick = (appState, { buttonType }) => trackEvent('TE_CLOSE_LA', `buttonType:${buttonType}`);
const teOnLoginFromLoginAssistantClick = (appState, { buttonType, redirectPath = '' }) => trackEvent('TE_LOGIN_FROM_LA', `buttonType:${buttonType}:uri:${redirectPath}`);
const teOnRedirectFromLoginAssistantClick = (appState, { buttonType, redirectPath = '' }) => trackEvent('TE_REDIRECT_FROM_LA', `buttonType:${buttonType}:uri:${redirectPath}`);
const teOnShowLoginAssistant = () => trackEvent('TE_SHOW_LA');

const evOnShowRewardsTransparencyPdp = appState => {
  const { cookies: { 'x-main': xMain }, pageView: { pageType }, sharedRewards: { transparencyPointsForItem, isEnrolled } } = appState;
  const isInTransparencyTest = isAssigned(HYDRA_RET, 1, appState);

  if (isInTransparencyTest && transparencyPointsForItem) {

    const { totalPoints: { points, dollarValue } = {} } = transparencyPointsForItem;

    // ZFC event
    const recognizedStatus = xMain ? 'recognized' : 'unrecognized';
    const enrolledStatus = isEnrolled ? 'enrolled' : 'unenrolled';
    trackEvent('TE_SHOW_REWARDS_TRANSPARENCY', `${pageType}:${points}:${recognizedStatus}:${enrolledStatus}`);

    // Amethyst event
    middlewareTrack({
      rewardsTransparencyImpression: {
        sourcePage: getAmethystPageType(pageType),
        isEnrolled: !!isEnrolled,
        rewardsPoints: points,
        rewardsDollars: dollarValue
      }
    });
  }
};

const evOnShowRewardsTransparencyCartModal = appState => {
  const { cookies: { 'x-main': xMain }, sharedRewards: { transparencyPointsForCart, isEnrolled } } = appState;
  const isInTransparencyTest = isAssigned(HYDRA_RET, 1, appState);

  if (isInTransparencyTest && transparencyPointsForCart) {

    const { totalPoints: { points, dollarValue } = {} } = transparencyPointsForCart;

    // ZFC event
    const recognizedStatus = xMain ? 'recognized' : 'unrecognized';
    const enrolledStatus = isEnrolled ? 'enrolled' : 'unenrolled';
    trackEvent('TE_SHOW_REWARDS_TRANSPARENCY', `cartModal:${points}:${recognizedStatus}:${enrolledStatus}}`);

    // Amethyst event
    middlewareTrack({
      rewardsTransparencyImpression: {
        sourcePage: 'CART_PAGE_MODAL',
        isEnrolled: !!isEnrolled,
        rewardsPoints: points,
        rewardsDollars: dollarValue
      }
    });
  }
};

export const evOnShowRewardsTransparencyClick = ({ isForCartModal, pageType, rewards, rewardsForCurrentPage, isRecognizedCustomer }) => {
  const { isEnrolled, transparencyPointsForCart } = rewards;
  const recognizedStatus = isRecognizedCustomer ? 'recognized' : 'unrecognized';
  const currentRewards = isForCartModal ? transparencyPointsForCart : rewardsForCurrentPage;
  const { totalPoints: { points, dollarValue } = {} } = currentRewards;
  const enrolledStatus = isEnrolled ? 'enrolled' : 'unenrolled';
  const view = isForCartModal ? 'cartModal' : pageType;

  trackEvent('TE_CLICK_REWARDS_TRANSPARENCY', `${view}:${points}:${recognizedStatus}:${enrolledStatus}`);

  return {
    rewardsTransparencyClick: {
      sourcePage: getAmethystPageType(pageType),
      isEnrolled: !!isEnrolled,
      rewardsPoints: points,
      rewardsDollars: dollarValue
    }
  };
};

// amethyst event: https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/StaySignedInModalInteraction.proto
export const evStaySignedInModalInteraction = interactionType => {
  // zfc event
  switch (interactionType) {
    case CLOSE_MODAL_BTN:
      trackEvent('TE_STAY_SIGNED_IN_MODAL_CLOSE_MODAL_BTN');
      break;
    case STAY_SIGNED_IN_BTN:
      trackEvent('TE_STAY_SIGNED_IN_MODAL_STAY_SIGNED_IN_BTN');
      break;
    case AUTO_LOGOUT:
      trackEvent('TE_STAY_SIGNED_IN_CLOSE_AUTO_LOGOUT');
      break;
    case CLOSE_MODAL_MISC: // if closed for any reason except button press or autologout
      trackEvent('TE_STAY_SIGNED_IN_CLOSE_MISC');
      break;
    case LOG_OUT_BTN:
      trackEvent('TE_STAY_SIGNED_IN_LOG_OUT_BTN');
  }

  middlewareTrack({
    staySignedInModalInteraction: {
      interactionType
    }
  });
};

// amethyst event: https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/StaySignedInModalView.proto
export const evOnShowStaySignedInModal = pageType => {
  const sourcePage = getAmethystPageType(pageType);

  // ZFC event
  trackEvent('TE_SHOW_STAY_SIGNED_IN_MODAL');

  // Amethyst event
  middlewareTrack({
    staySignedInModalView: {
      sourcePage
    }
  });
};

// LogInAssistantBtnType
export const evLogInAssistantInteraction = (appState, { buttonType, redirectPath = '' }) => {
  middlewareTrack({
    logInAssistantButtonType: {
      buttonType,
      buttonPageUri: redirectPath
    }
  });
};

export const evSponsoredProductClick = (state, action) => {
  const { index, styleId, adId, searchTerm } = action.payload;

  trackEvent('TE_SPONSORED_PRODUCTS_CLICK', `styleId:${styleId}productIndex:${index}:searchTerm:${searchTerm}:adId:${adId}`);

  middlewareTrack({
    advertisementClick: {
      pageType: 'SEARCH_PAGE',
      advertisementType: 'SPONSORED_PRODUCT',
      sponsoredProuductDetails: {
        productIndex: index,
        productIdentifier: {
          styleId: Number(styleId)
        },
        adId
      // bidderName // Currently, this info isn't available in CC call, but it's on the Amethyst proto, so add when available
      }
    }
  });
};

export const evSponsoredProductView = (state, action) => {
  const { searchTerm } = action.payload;
  const { search: { sponsoredProducts, sponsoredProducts: { adId } } } = state;
  const styleIds = sponsoredProducts.styles.map(style => style.styleId);
  const impressions = sponsoredProducts?.styles.map((product, index) => ({
    pageType: 'SEARCH_PAGE',
    advertisementType: 'SPONSORED_PRODUCT',
    sponsoredProductDetails: {
      productIndex: index,
      productIdentifier: {
        styleId: Number(product.styleId)
      },
      adId
    }
    // bidderName // Currently, this info isn't available in CC call, but it's on the Amethyst proto, so add when available
  }));

  trackEvent('TE_SPONSORED_PRODUCTS_VIEW', `styles:${styleIds}:searchTerm:${searchTerm}:adId:${adId}`);

  middlewareTrack({
    advertisementImpressionWrapper: {
      advertisementImpression: impressions
    }
  });
};

const evSponsoredProductEvent = (state, action) => {
  if (action.payload?.eventType === 'click') {
    evSponsoredProductClick(state, action);
  } else if (action.payload?.eventType === 'view') {
    evSponsoredProductView(state, action);
  }
};

export default {
  events: {
    [SEARCH_FEEDBACK_CLICK]: [evFeedbackClick],
    [CLOSE_LA]: [evLogInAssistantInteraction, teOnCloseLoginAssistantClick],
    [LOGIN_FROM_LA]: [evLogInAssistantInteraction, teOnLoginFromLoginAssistantClick],
    [REDIRECT_FROM_LA]: [evLogInAssistantInteraction, teOnRedirectFromLoginAssistantClick],
    [SHOW_LA]: [teOnShowLoginAssistant],
    [STORE_REWARDS_TRANSPARENCY_POINTS_FOR_ITEM]: [evOnShowRewardsTransparencyPdp],
    [EVENT_CART_MODAL_VIEW]: [evOnShowRewardsTransparencyCartModal],
    [SPONSORED_PRODUCT_EVENT]: [evSponsoredProductEvent]
  }
};
